<template>
  <n-card title="Dépenses par catégorie / mois">
    <n-space vertical>
      <n-space>
        <n-date-picker v-model:value="startTs" type="date" placeholder="From" @update:value="load" />
        <n-date-picker v-model:value="endTs"   type="date" placeholder="To"   @update:value="load" />
      </n-space>

      <div v-if="hasData" style="height: 360px">
        <Bar :data="chartData" :options="chartOptions" />
      </div>
      <n-empty v-else description="Aucune transaction sur cette période" />
    </n-space>
  </n-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTransactionStore } from '../stores/transactionStore';

ChartJS.register(BarElement, BarController, CategoryScale, LinearScale, Tooltip, Legend);

const store = useTransactionStore();

const CATEGORY_COLORS = {
  Alimentation: '#3b8c5a',
  Transport:    '#2563eb',
  Logement:     '#7c3aed',
  'Santé':      '#dc2626',
  Loisirs:      '#d97706',
  Abonnements:  '#0891b2',
  Maison:       '#b45309',
  Autre:        '#6b7280',
};

const now = new Date();
const startTs = ref(new Date(now.getFullYear(), 0, 1).getTime());
const endTs   = ref(new Date(now.getFullYear(), 11, 31).getTime());
const toIso = (ts) => new Date(ts).toISOString().split('T')[0];

const load = () => store.fetchCategoryData(toIso(startTs.value), toIso(endTs.value));
onMounted(load);

const hasData = computed(() => store.categoryData?.length > 0);

const chartData = computed(() => {
  const raw = store.categoryData ?? [];

  const months = [...new Set(raw.map(r => r.month))].sort();
  const categories = [...new Set(raw.map(r => r.category))];

  const datasets = categories.map(cat => {
    const color = CATEGORY_COLORS[cat] ?? '#6b7280';
    return {
      label: cat,
      backgroundColor: color,
      data: months.map(m => {
        const row = raw.find(r => r.month === m && r.category === cat);
        return row ? row.total : 0;
      }),
    };
  });

  return { labels: months, datasets };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { color: '#ccc' } },
    tooltip: {
      callbacks: {
        label: (ctx) =>
          ` ${ctx.dataset.label}: ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(ctx.raw)}`,
      },
    },
  },
  scales: {
    x: { stacked: true },
    y: { stacked: true, beginAtZero: true },
  },
};
</script>
